import React from 'react'

import { Button, Tooltip, LoadingIcon } from '@clickcease/cc-client-components-lib'
import {
  CONNECTION_STATE,
  CONNECTION_TO_VARIANT,
  LABEL_VARIANT,
  ProductType,
  RISKS_TO_LABEL_VARIANT,
} from 'Types/Product'
import {
  Title,
  ProductStyle,
  TextContainer,
  Divider,
  BoldText,
  Label,
  LabelsContainer,
} from './Product.styled'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ConnectionStatusPm } from './ConnectionStatusPm'
import { ConnectionStatusBz } from './ConnectionStatusBz'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { use12InstallmentsAllowChanges } from 'Hooks/split/use12InstallmentsChanges'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import { useSplit } from 'Hooks/useSplit'
import { formatNumber } from 'Utils/number'

type ProductProps = {
  content: ProductType
  id?: string
  domainsRef?: React.RefObject<HTMLDivElement>
  statusIsLoading?: boolean
  visitsIsLoading?: boolean
}

export const Product = ({ content, statusIsLoading, visitsIsLoading, id }: ProductProps) => {
  const { t } = useTranslation('home')
  const navigate = useNavigate()
  const isPlatformStatusEnabled = useSplit('CE_PLATFORM_STATUS')
  const { isInstallmentsSubscription } = useUserStore()
  const isTrialSubscription = useIsTrialSubscription()
  const split12InstallmentsAllowChanges = use12InstallmentsAllowChanges()
  const { setModalToShow } = useAppStore()
  const { owner } = useUserStore()

  const labelColor: LABEL_VARIANT | undefined = CONNECTION_TO_VARIANT.find(
    (connection) => content.connected === connection.key
  )?.background

  const riskLabelColor: LABEL_VARIANT | undefined = RISKS_TO_LABEL_VARIANT.find(
    (risk) => content.riskLevel === risk.key
  )?.background

  const isConnected = content.connected !== CONNECTION_STATE.NOT_CONNECTED
  const buttonText = isConnected ? t('product.go_to_dashboard') : t('product.setup')

  const shouldShowConnectionStatus = content.connected !== CONNECTION_STATE.PARTIALLY_CONNECTED

  const handleConnectPlatform = () => {
    if (content.id === 'paidMarketing') {
      setModalToShow(GLOBAL_MODALS.PLATFORM_CONNECTION)
    } else if (content.id === 'botZapping') {
      navigate('/domain-management')
    }
  }
  const showTooltipForInstallmentsNotOnTrialNoChangesAllowed =
    !content.enabled &&
    !isTrialSubscription &&
    isInstallmentsSubscription &&
    !split12InstallmentsAllowChanges

  return (
    <>
      {showTooltipForInstallmentsNotOnTrialNoChangesAllowed ? (
        <Tooltip tooltiptContent={t('product.disabled_message_schedule_meeting')}>
          <ProductStyle variant="disabled">
            {content.image}
            <Title variant="disabled">{content.title}</Title>
          </ProductStyle>
        </Tooltip>
      ) : (
        <ProductStyle id={id} variant={!content.enabled ? 'disabled' : undefined}>
          <>{content.image}</>
          <Title variant={!content.enabled ? 'disabled' : undefined}>{content.title}</Title>
          {content.enabled && (
            <>
              <TextContainer>
                <div>
                  {t('product.invalid_visits')}:{' '}
                  {visitsIsLoading ? (
                    <LoadingIcon width={'20px'} height={'20px'} />
                  ) : (
                    <Tooltip tooltiptContent={content.blocks} side="bottom">
                      <BoldText>{formatNumber(content.blocks)}</BoldText>
                    </Tooltip>
                  )}
                </div>
                <Divider />
                <div>
                  {t('product.invalid')}:{' '}
                  {visitsIsLoading ? (
                    <LoadingIcon width={'20px'} height={'20px'} />
                  ) : (
                    <BoldText>{content.invalidPercentage}%</BoldText>
                  )}
                </div>
              </TextContainer>
              <LabelsContainer>
                {statusIsLoading ? (
                  <LoadingIcon width={'50px'} height={'50px'} />
                ) : (
                  <>
                    {shouldShowConnectionStatus && (
                      <Label variant={labelColor}>{content.connected}</Label>
                    )}
                    {content.connected !== CONNECTION_STATE.NOT_CONNECTED && (
                      <Label variant={riskLabelColor}>
                        {t('risk_level')} <BoldText> {content.riskLevel}</BoldText>
                      </Label>
                    )}
                  </>
                )}
              </LabelsContainer>
              {isPlatformStatusEnabled && (
                <>
                  {content.id === 'paidMarketing' ? (
                    <ConnectionStatusPm product={content.id} />
                  ) : (
                    <ConnectionStatusBz />
                  )}
                </>
              )}
              <Button
                variant="ghost"
                text={buttonText}
                onClick={() => {
                  isConnected ? navigate(content.link) : handleConnectPlatform()
                }}
                disabled={!isConnected || !owner}
              />
            </>
          )}
        </ProductStyle>
      )}
    </>
  )
}
